import { createApiRequest, setPathParams } from '../../utils/request'
import { CurrentUserApi } from '../current-user'

import { type ApiResponseCode, type ApiSchemaRequest, type ApiSchemaResponse, createSchemaApiRequest, hasResponseStatus } from '../schema'

export class Broker$CurrentUserApi extends CurrentUserApi {
  setUserToken (token: string | undefined) {
    this.ctx.options.userAuthToken = token
    if (token != null) this.ctx.eventEmitter.emit('loginSuccess')
  }

  saveTokenToStorage () {
    if (this.ctx.options.userAuthToken == null) return
    window.localStorage.setItem(this.ctx.options.userAuthTokenStorageKey, this.ctx.options.userAuthToken)
  }

  async loginByStoredToken (): Promise<ApiSchemaResponse<'/currentuser', 'get'> | undefined> {
    const token = window.localStorage.getItem(this.ctx.options.userAuthTokenStorageKey)
    if (token == null) throw new Error('Token not found in storage')
    try {
      return await this.loginByAuthToken({ token })
    } catch (e) {
      throw new Error('Failed to load user by the token from storage')
    }
  }

  removeTokenFromStorage () {
    window.localStorage.removeItem(this.ctx.options.userAuthTokenStorageKey)
  }

  getUserToken (): string | undefined {
    return this.ctx.options.userAuthToken
  }

  async getCurrentUser (config?: ApiSchemaRequest<'/currentuser', 'get'>): Promise<ApiSchemaResponse<'/currentuser', 'get'>> {
    const res = await createSchemaApiRequest({
      path: '/currentuser',
      method: 'get',
      ctx: this.ctx,
      options: { disableAuth: true }
    })
    return res
  }

  async loginLocal (config: ApiSchemaRequest<'/auth/local', 'post'>): Promise<ApiSchemaResponse<'/auth/local', 'post'>> {
    const res = await createSchemaApiRequest({
      path: '/auth/local',
      method: 'post',
      ctx: this.ctx,
      data: config.data,
      options: { disableAuth: true }
    })
    if (hasResponseStatus(res, 200) && res.data.jwt != null) {
      this.setUserToken(res.data.jwt)
    }
    return res
  }

  isLoggedIn (): boolean {
    return this.ctx.options.userAuthToken != null
  }

  // TODO: fix schema
  async loginByProvider (config: ApiSchemaRequest<'/connect/*', 'get'>): Promise<ApiSchemaResponse<'/connect/*', 'get'>> {
    const path: string = setPathParams('/connect/{provider}', config.params)

    const response = await createApiRequest({
      ctx: this.ctx,
      path,
      method: 'get',
      options: { disableAuth: true }
    })

    return {
      status: response.status as ApiResponseCode<'/connect/*', 'get'>,
      data: response.data
    }
  }

  /**
   *
   * @see {@link getRequest}
   * {@link ApiSchemaRequest}
   * TODO: check how this works. Provider logins initiates redirect
   * @param config
   * @returns
   */
  async loginByProviderAccessToken (config: ApiSchemaRequest<'/auth/{provider}/callback', 'get'>): Promise<ApiSchemaResponse<'/auth/{provider}/callback', 'get'>> {
    const res = await createSchemaApiRequest({
      path: '/auth/{provider}/callback',
      method: 'get',
      params: config.params,
      ctx: this.ctx,
      query: config.query,
      options: { disableAuth: true }
    })
    if (hasResponseStatus(res, 200) && res.data.jwt != null) {
      this.setUserToken(res.data.jwt)
    }
    return res
  }

  async loginByAuthToken (config: { token: string }) {
    const res = await createSchemaApiRequest({
      path: '/currentuser',
      method: 'get',
      ctx: this.ctx,
      options: { userAuthToken: config.token }
    })
    if (hasResponseStatus(res, 200) && config.token != null) {
      this.setUserToken(config.token)
    }
    return res
  }

  async logout (): Promise<ApiSchemaResponse<'/auth/logout', 'get'>> {
    return await createSchemaApiRequest({
      path: '/auth/logout',
      method: 'get',
      ctx: this.ctx,
      options: { disableAuth: true }
    })
  }
}
